import { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck, ShieldX, Loader2 } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { authService } from '../../services';
import { messageHandler } from '../../services/message-handler.service';
import { Card } from '../ui/Card';
import Button from '../ui/Button';

function TwoFactorSettings() {
  const queryClient = useQueryClient();
  const [setupData, setSetupData] = useState<{ secret: string; otpauthUrl: string } | null>(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [showDisableForm, setShowDisableForm] = useState(false);

  const { data: statusData, isLoading: statusLoading } = useQuery({
    queryKey: ['2fa-status'],
    queryFn: () => authService.getTwoFactorStatus(),
  });

  const twoFactorEnabled = statusData?.data?.enabled ?? false;

  const setupMutation = useMutation({
    mutationFn: () => authService.setupTwoFactor(),
    onSuccess: response => {
      if (response.success && response.data) {
        setSetupData({
          secret: response.data.secret,
          otpauthUrl: response.data.otpauthUrl,
        });
      } else {
        messageHandler.error(response.message || 'Erro ao gerar QR code');
      }
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || error.message || 'Erro ao configurar 2FA');
    },
  });

  const verifyMutation = useMutation({
    mutationFn: () => authService.verifyTwoFactorSetup(verifyCode),
    onSuccess: response => {
      if (response.success) {
        messageHandler.success('Autenticação de dois fatores ativada com sucesso!');
        setSetupData(null);
        setVerifyCode('');
        queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      } else {
        messageHandler.error(response.message || 'Código inválido');
      }
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || error.message || 'Código inválido');
    },
  });

  const disableMutation = useMutation({
    mutationFn: () => authService.disableTwoFactor(disableCode),
    onSuccess: response => {
      if (response.success) {
        messageHandler.success('Autenticação de dois fatores desativada.');
        setDisableCode('');
        setShowDisableForm(false);
        queryClient.invalidateQueries({ queryKey: ['2fa-status'] });
      } else {
        messageHandler.error(response.message || 'Código inválido');
      }
    },
    onError: (error: any) => {
      messageHandler.error(error.response?.data?.error || error.message || 'Código inválido');
    },
  });

  const handleStartSetup = () => {
    setVerifyCode('');
    setSetupData(null);
    setupMutation.mutate();
  };

  const handleCancelSetup = () => {
    setSetupData(null);
    setVerifyCode('');
  };

  if (statusLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary-50 dark:bg-primary-900/20">
          <ShieldCheck className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Autenticação de Dois Fatores
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Proteja sua conta com uma camada extra de segurança
          </p>
        </div>
      </div>

      {twoFactorEnabled && !showDisableForm && !setupData && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <ShieldCheck className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-green-900 dark:text-green-100">
                2FA ativado
              </p>
              <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                Sua conta está protegida com autenticação de dois fatores. Você precisará informar um código do seu app autenticador a cada login.
              </p>
            </div>
          </div>

          <Button variant="danger" onClick={() => setShowDisableForm(true)}>
            <ShieldX className="h-4 w-4 mr-2" />
            Desativar 2FA
          </Button>
        </div>
      )}

      {twoFactorEnabled && showDisableForm && (
        <div className="space-y-4">
          <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-red-700 dark:text-red-300">
              Para desativar o 2FA, digite o código atual do seu aplicativo autenticador para confirmar.
            </p>
          </div>

          <div>
            <label htmlFor="disable-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Código de verificação
            </label>
            <input
              id="disable-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={disableCode}
              onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm sm:text-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
            />
          </div>

          <div className="flex gap-3">
            <Button
              variant="danger"
              onClick={() => disableMutation.mutate()}
              isLoading={disableMutation.isPending}
              disabled={disableCode.length !== 6}
            >
              Confirmar desativação
            </Button>
            <Button variant="secondary" onClick={() => { setShowDisableForm(false); setDisableCode(''); }}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {!twoFactorEnabled && !setupData && (
        <div className="space-y-4">
          <div className="flex items-start gap-3 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <ShieldX className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium text-gray-900 dark:text-gray-100">
                2FA desativado
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Ative a autenticação de dois fatores para maior segurança. Use apps como Google Authenticator, Microsoft Authenticator ou Authy.
              </p>
            </div>
          </div>

          <Button onClick={handleStartSetup} isLoading={setupMutation.isPending}>
            <ShieldCheck className="h-4 w-4 mr-2" />
            Ativar 2FA
          </Button>
        </div>
      )}

      {!twoFactorEnabled && setupData && (
        <div className="space-y-6">
          <div className="text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Escaneie o QR code abaixo com seu aplicativo autenticador
            </p>
            <div className="inline-block p-4 bg-white rounded-xl border border-gray-200">
              <QRCodeSVG value={setupData.otpauthUrl} size={200} level="M" />
            </div>
          </div>

          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
              Ou digite manualmente esta chave:
            </p>
            <p className="font-mono text-sm text-gray-900 dark:text-gray-100 break-all select-all">
              {setupData.secret}
            </p>
          </div>

          <div>
            <label htmlFor="verify-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Digite o código de 6 dígitos do seu app autenticador
            </label>
            <input
              id="verify-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={verifyCode}
              onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              className="block w-full max-w-xs rounded-md border-gray-300 shadow-sm sm:text-sm focus:border-primary-500 focus:ring-primary-500 dark:bg-gray-800 dark:border-gray-600 dark:text-white tracking-widest text-center text-lg font-bold"
            />
          </div>

          <div className="flex gap-3">
            <Button
              onClick={() => verifyMutation.mutate()}
              isLoading={verifyMutation.isPending}
              disabled={verifyCode.length !== 6}
            >
              Confirmar ativação
            </Button>
            <Button variant="secondary" onClick={handleCancelSetup}>
              Cancelar
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

export default TwoFactorSettings;
